import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone, LineChart } from "lucide-react";
import { useLang } from "@/i18n";

const HERO_BG =
  "https://images.unsplash.com/photo-1707567019592-a05a41cc0b52?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMG1ldGFsbGljJTIwM2QlMjBsdXh1cnklMjBkYXJrfGVufDB8fHx8MTc4NzAxNTgwMnww&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { str } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden noise"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img src={HERO_BG} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0A0A0A]/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/20 to-[#0A0A0A]/50" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 px-6 sm:px-10 lg:px-16 pb-12 lg:pb-16 pt-44">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.25, duration: 0.9, ease: "easeOut" }}
          className="mb-6 lg:mb-10 flex items-center gap-3"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00C851] opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#00C851]" />
          </span>
          <span data-testid="hero-live-badge" className="font-ticker text-[11px] sm:text-xs tracking-[0.4em] text-[#25D366] uppercase">
            {str.heroBadge}
          </span>
        </motion.div>

        <h1 className="font-display font-black uppercase leading-[0.85] tracking-tighter text-[17vw] sm:text-[14vw] lg:text-[11rem]">
          <MaskedLine delay={0.4}>Mahima</MaskedLine>
          <MaskedLine delay={0.58}>
            <span className="text-stroke-gold">Metals</span>
          </MaskedLine>
        </h1>

        <div className="mt-8 lg:mt-12 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-md text-sm sm:text-base leading-relaxed text-white/60"
          >
            {str.heroTag1} <span className="font-semibold text-[#C0C0C0]">Aluminium</span> {str.heroTag2}{" "}
            <span className="font-semibold text-[#B87333]">Copper</span> {str.heroTag3}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4 w-full sm:w-auto"
          >
            <a
              href="#rates"
              data-testid="hero-view-rates-btn"
              className="group inline-flex items-center justify-center gap-3 bg-[#25D366] text-[#0A0A0A] px-7 py-4 font-ticker text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-[#7BE3A6] transition-colors duration-300"
            >
              <LineChart size={15} /> {str.heroViewRates}
            </a>
            <a
              href="tel:+918000093073"
              data-testid="hero-call-btn"
              className="inline-flex items-center justify-center gap-3 border border-white/25 px-7 py-4 font-ticker text-[11px] tracking-[0.25em] uppercase text-white/85 hover:border-[#25D366] hover:text-[#25D366] transition-colors duration-300"
            >
              <Phone size={15} /> +91 80000 93073
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute right-6 sm:right-10 lg:right-16 top-1/2 -translate-y-1/2 z-10 hidden md:flex flex-col items-center gap-4"
      >
        <span className="font-ticker text-[10px] tracking-[0.4em] uppercase text-white/40 [writing-mode:vertical-rl]">
          Scroll
        </span>
        <motion.span animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown size={16} className="text-[#25D366]" />
        </motion.span>
      </motion.div>
    </section>
  );
}
